/* Asiakkaat, varaukset ja markkinointi widgets. */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import SmsIcon from '@mui/icons-material/Sms';
import TireRepairIcon from '@mui/icons-material/TireRepair';

import { BookingActionDialog } from '../../booking/BookingActions';
import { dataSources } from '../dataSources';
import { useWidgetFrame } from '../frameContext';
import { invalidate } from '../storeDashboardData';
import BreakdownTile from '../templates/BreakdownTile';
import FeedbackBody from '../templates/FeedbackBody';
import KpiTile from '../templates/KpiTile';
import ListBody, { SummaryAction, SummaryText } from '../templates/ListBody';
import { fmtDay, fmtDayLong, fmtNumber, fmtTime, todayIso } from '../templates/format';
import { listOf, useHeader, useSource } from '../widgetHooks';

const bookingWhen = (booking) => {
  const day = fmtDayLong(booking.booking_date);
  if (booking.booking_time) return `${day} ${String(booking.booking_time).slice(0, 5)}`;
  if (booking.drop_off_window_name) return `${day} ${booking.drop_off_window_name}`;
  return day;
};

export function BookingsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const frame = useWidgetFrame();
  const today = todayIso();
  const counts = useSource(dataSources.bookingsCounts());
  const upcoming = useSource(dataSources.bookingsUpcoming(today));
  const pending = useSource(dataSources.bookingsPending());
  const [chip, setChip] = useState('all');
  const [rejecting, setRejecting] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const refresh = () => invalidate(['bookings:', 'stats']);

  const act = async (booking, action) => {
    setBusyId(booking.id);
    try {
      await axios.post(`/api/bookings/${booking.id}/${action}/`);
      refresh();
    } catch (error) {
      /* the page-level snackbar pattern is per component; the row simply stays until the next refresh */
    } finally {
      setBusyId(null);
      setRejecting(null);
    }
  };
  const openBooking = async (booking) => {
    if (!booking.seen_at) {
      try {
        await axios.post(`/api/bookings/${booking.id}/mark_seen/`);
        refresh();
      } catch (error) {
        /* still navigate */
      }
    }
    navigate(`/bookings?booking=${booking.id}`);
  };
  const markAllSeen = async () => {
    try {
      await axios.post('/api/bookings/mark_all_seen/');
      refresh();
    } catch (error) {
      /* ignore, next poll shows the truth */
    }
  };

  const unseen = counts.data?.unseen ?? 0;
  const pendingCount = counts.data?.pending ?? listOf(pending.data).length;
  const source = chip === 'pending' ? listOf(pending.data) : listOf(upcoming.data);
  const rows = source.map((booking) => {
    const isNew = !booking.seen_at;
    const isPending = booking.status === 'pending';
    return {
      key: booking.id,
      testId: `widget-bookings-row-${booking.id}`,
      icon: EventAvailableIcon,
      iconColor: isPending ? '#ed8936' : '#94a3b8',
      plate: booking.vehicle_registration,
      primary: booking.customer_name,
      bold: isNew,
      secondary: [booking.service_name, bookingWhen(booking)].filter(Boolean).join(' · '),
      chip: isNew ? t('dashboard.actions.newPill') : null,
      chipTone: 'accent',
      action: isPending
        ? { label: t('dashboard.actions.confirm'), variant: 'primary', busy: busyId === booking.id, onClick: () => act(booking, 'confirm'), testId: `booking-confirm-${booking.id}` }
        : { label: t('dashboard.actions.open'), onClick: () => openBooking(booking) },
      action2: isPending ? { label: t('dashboard.actions.decline'), variant: 'quiet', busy: busyId === booking.id, onClick: () => setRejecting(booking), testId: `booking-reject-${booking.id}` } : null,
      onClick: () => openBooking(booking),
    };
  });
  const loaded = Boolean(counts.data) && Boolean(upcoming.data);
  useHeader({ link: { labelKey: 'dashboard.actions.openBookings', to: '/bookings' }, empty: loaded && rows.length === 0 && unseen === 0 }, [loaded, rows.length, unseen]);

  if (frame?.headless) {
    return (
      <BreakdownTile
        icon={EventAvailableIcon}
        value={pendingCount}
        unit={t('dashboard.chips.awaitingConfirmation')}
        chips={[
          { n: unseen, label: t('dashboard.chips.new'), color: '#3182ce', bg: 'rgba(49,130,206,0.10)' },
          { n: counts.data?.today ?? 0, label: t('dashboard.chips.today').toLowerCase(), color: '#4299e1', bg: '#ebf4ff' },
        ]}
        onClick={() => navigate('/bookings')}
      />
    );
  }

  const summary = counts.data ? (
    <>
      <SummaryText parts={[{ strong: true, text: t('dashboard.chips.newCount', { count: unseen }) }, { text: t('dashboard.chips.pendingCount', { count: pendingCount }) }, { text: t('dashboard.chips.todayCount', { count: counts.data.today ?? 0 }) }]} />
      {unseen > 0 ? <SummaryAction label={t('dashboard.actions.markAllSeen')} onClick={markAllSeen} testId="booking-mark-all-seen" /> : null}
    </>
  ) : null;

  return (
    <>
      <ListBody
        rows={rows}
        chips={[
          { key: 'all', label: t('dashboard.chips.all') },
          { key: 'pending', label: t('dashboard.chips.pending'), n: pendingCount, state: 'waiting' },
        ]}
        activeChip={chip}
        onChip={setChip}
        summary={summary}
        onMore={() => navigate('/bookings')}
        testIdPrefix="widget-bookings"
      />
      {rejecting && !frame?.inPreview ? (
        <BookingActionDialog
          open
          onClose={() => setRejecting(null)}
          title={t('booking.actions.reject_title')}
          message={t('booking.actions.reject_message')}
          confirmLabel={t('booking.actions.reject')}
          confirmColor="error"
          onConfirm={() => act(rejecting, 'reject')}
          loading={busyId === rejecting.id}
        />
      ) : null}
    </>
  );
}

export function FeedbackWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.feedbackStats());
  const rating = data?.average_rating_30d ?? data?.average_rating_all_time;
  const distribution = data?.rating_distribution ? [1, 2, 3, 4, 5].map((stars) => Number(data.rating_distribution[stars] ?? data.rating_distribution[String(stars)] ?? 0)) : null;
  useHeader({ link: { labelKey: 'dashboard.actions.openFeedback', to: '/customer-feedback' }, empty: Boolean(data) && !data.total_responses_all_time }, [data]);
  return (
    <FeedbackBody
      rating={rating != null ? Number(rating).toFixed(1).replace('.', ',') : '–'}
      sub={`30 ${t('dashboard.chips.daysShort')} · ${t('dashboard.chips.responseRate', { rate: Math.round(Number(data?.response_rate_30d ?? 0)) })}`}
      callbacks={{ n: data?.pending_low_rating_followups_count ?? 0, label: t('dashboard.chips.callbacksCount', { count: data?.pending_low_rating_followups_count ?? 0 }), onClick: () => navigate('/customer-feedback') }}
      distribution={distribution}
    />
  );
}

export function CallbacksWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.feedbackCallbacks());
  const [busyId, setBusyId] = useState(null);
  const items = listOf(data);
  const markHandled = async (response) => {
    setBusyId(response.id);
    try {
      await axios.post(`/api/feedback-responses/${response.id}/mark_followup_actioned/`);
      invalidate('fb:');
    } catch (error) {
      /* leave the row */
    } finally {
      setBusyId(null);
    }
  };
  useHeader({ link: { labelKey: 'dashboard.actions.openFeedback', to: '/customer-feedback' }, empty: Boolean(data) && items.length === 0 }, [data, items.length]);
  const rows = items.map((response) => ({
    key: response.id,
    icon: PhoneCallbackIcon,
    iconColor: '#c53030',
    primary: response.customer_name || '',
    secondary: `★ ${response.rating}${response.comment ? ` · ”${response.comment}”` : ''}`,
    action: { label: t('dashboard.actions.markHandled'), busy: busyId === response.id, onClick: () => markHandled(response) },
    onClick: () => navigate('/customer-feedback'),
  }));
  return <ListBody rows={rows} onMore={() => navigate('/customer-feedback')} />;
}

export function RemindersWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const upcoming = useSource(dataSources.remindersUpcoming());
  const items = useMemo(() => listOf(upcoming.data).slice(0, 25), [upcoming.data]);
  const weekAhead = items.filter((item) => item.due_date && new Date(item.due_date) - Date.now() < 7 * 86400000).length;
  useHeader({ link: { labelKey: 'dashboard.actions.openReminders', to: '/service-reminders' }, empty: Boolean(upcoming.data) && items.length === 0 }, [upcoming.data, items.length]);
  const rows = items.map((item, index) => ({
    key: index,
    icon: item.channel === 'sms' ? SmsIcon : item.channel === 'email' ? EmailIcon : NotificationsActiveIcon,
    plate: item.vehicle_registration,
    primary: item.customer_name || '',
    secondary: item.rule_name || '',
    right: fmtDayLong(item.due_date),
    rightMono: false,
    rightTone: 'muted',
  }));
  const summary = upcoming.data ? <SummaryText parts={[{ strong: true, text: t('dashboard.chips.leaving', { count: weekAhead }) }, { text: `7 ${t('dashboard.chips.daysShort')}` }]} /> : null;
  return <ListBody rows={rows} summary={summary} onMore={() => navigate('/service-reminders')} />;
}

export function AiPhoneWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.aiPhoneNew());
  const items = listOf(data)
    .slice()
    .sort((a, b) => (b.urgency === 'urgent') - (a.urgency === 'urgent'));
  useHeader({ count: items.length || null, link: { labelKey: 'dashboard.actions.openCalls', to: '/ai-phone' }, empty: Boolean(data) && items.length === 0 }, [data, items.length]);
  const rows = items.map((call) => {
    const urgent = call.urgency === 'urgent';
    const phone = call.effective_callback_number || call.caller_phone;
    return {
      key: call.id,
      icon: CallIcon,
      iconColor: urgent ? '#c53030' : '#3182ce',
      primary: call.issue_description || call.caller_name || phone || '',
      secondary: [call.caller_name, call.vehicle_registration, fmtTime(call.called_at)].filter(Boolean).join(' · '),
      chip: urgent ? t('dashboard.chips.urgent') : t('dashboard.chips.new'),
      chipTone: urgent ? 'red' : 'accent',
      action: phone ? { label: t('dashboard.actions.call'), onClick: () => window.open(`tel:${phone}`, '_self') } : null,
      onClick: () => navigate('/ai-phone'),
    };
  });
  return <ListBody rows={rows} onMore={() => navigate('/ai-phone')} />;
}

export function TireHotelWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const capacity = useSource(dataSources.tireCapacity());
  const flagged = useSource(dataSources.tireFlagged());
  const flaggedCount = listOf(flagged.data).length;
  useHeader({ link: { labelKey: 'dashboard.actions.openTireHotel', to: '/tire-hotel' } }, []);
  return (
    <BreakdownTile
      icon={TireRepairIcon}
      value={`${Math.round(Number(capacity.data?.occupancy_percentage ?? 0))} %`}
      unit={t('dashboard.chips.occupancy')}
      chips={[
        { n: fmtNumber(capacity.data?.occupied ?? 0), label: `/ ${fmtNumber(capacity.data?.total ?? 0)} ${t('dashboard.chips.slots')}` },
        { n: flaggedCount, label: t('dashboard.chips.setsToReplace'), color: '#ed8936', bg: '#fef3e2' },
        ...(capacity.data?.capacity_alert ? [{ n: '!', label: t('dashboard.chips.capacityAlert'), color: '#c53030', bg: '#fee2e2' }] : []),
      ]}
      rows={[{ icon: TireRepairIcon, label: t('dashboard.actions.sendCampaign'), value: '→' }]}
      onClick={() => navigate('/tire-hotel/pipeline')}
    />
  );
}

export function NewCustomersTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.customersReport('month'));
  return <KpiTile icon={PersonAddIcon} value={data?.new_this_period ?? 0} label={t('dashboard.widgets.newcust.title')} sub={t('dashboard.chips.thisMonth')} onClick={() => navigate('/customers')} />;
}

export const _debug = { fmtDay };
